import { useState, useEffect } from 'react';
import { BellRing, ExternalLink, Trash2 } from 'lucide-react';
import { subscriptionService } from '../services/api';
import CarThumbnail from './CarThumbnail';
import PriceChart from './PriceChart';

const Seguimiento = () => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  const load = async (p = 1) => {
    setLoading(p === 1);
    try {
      const data = await subscriptionService.getAll(p);
      if (p === 1) setSubs(data.subscriptions);
      else setSubs(prev => [...prev, ...data.subscriptions]);
      setPagination(data.pagination);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(1); }, []);

  const handleUnsubscribe = async (vehicleId) => {
    setSubs(prev => prev.filter(s => s.vehicle_id !== vehicleId));
    try { await subscriptionService.unsubscribe(vehicleId); }
    catch { load(1); }
  };

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    load(next);
  };

  const formatPrice = (price, currency) => {
    const n = new Intl.NumberFormat('es-AR', { minimumFractionDigits: 0 }).format(Math.round(price));
    return currency === 'ARS' ? `$ ${n}` : `US$ ${n}`;
  };

  if (loading) return (
    <div className="flex justify-center py-20">
      <div className="w-6 h-6 border-2 border-[#7b8fa8] border-t-transparent rounded-full animate-spin"></div>
    </div>
  );

  if (!subs.length) return (
    <div className="text-center py-20">
      <BellRing className="w-10 h-10 text-[#8a8577] mx-auto mb-3" />
      <p className="text-[#8a8577]">No estás siguiendo ningún vehículo</p>
      <p className="text-[#8a8577] text-sm mt-1">Usá la campanita en cada vehículo para recibir alertas de precio</p>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-[#e8e4de]">Vehículos en seguimiento</h2>
          <p className="text-xs text-[#8a8577] mt-0.5">{pagination?.total || subs.length} vehículos</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {subs.map(sub => (
          <div key={sub.id} className="bg-[#1c1c1a] rounded-lg border border-[#2a2926] overflow-hidden hover:border-[#3a3835] transition-all duration-200">
            <div className="relative">
              <CarThumbnail imageUrl={sub.pictures} title={sub.title} className="rounded-t-lg" />
              {sub.is_active === false && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="text-xs text-[#e8e4de] bg-[#c75d5d]/80 px-2 py-1 rounded">Despublicado</span>
                </div>
              )}
              <span className={`absolute top-2 left-2 text-[9px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded ${sub.vehicle_type === 'moto' ? 'bg-[#48a877]/20 text-[#48a877]' : 'bg-[#7b8fa8]/20 text-[#7b8fa8]'}`}>
                {sub.vehicle_type === 'moto' ? 'Moto' : 'Auto'}
              </span>
            </div>

            <div className="p-3">
              <h3 className="text-sm font-medium text-[#e8e4de] line-clamp-2 leading-snug">{sub.title || 'Vehículo'}</h3>
              <p className="text-lg font-mono font-bold text-[#e8e4de] mt-1.5">
                {formatPrice(sub.current_price, sub.currency_id)}
              </p>

              {/* Price evolution chart */}
              {sub.price_history && sub.price_history.length > 1 && (
                <div className="mt-2">
                  <PriceChart priceHistory={sub.price_history} currentPrice={sub.current_price} currency={sub.currency_id} />
                </div>
              )}

              <p className="text-[10px] text-[#8a8577] mt-2">
                Siguiendo desde {new Date(sub.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: 'short' })}
              </p>

              <div className="flex items-center gap-2 mt-3">
                {sub.is_active !== false && sub.permalink && (
                  <a
                    href={sub.permalink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs text-[#7b8fa8] border border-[#2a2926] rounded hover:border-[#3a3835] transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <ExternalLink className="w-3 h-3" />
                    Ver
                  </a>
                )}
                <button
                  onClick={() => handleUnsubscribe(sub.vehicle_id)}
                  className="p-1.5 text-[#8a8577] hover:text-[#c75d5d] transition-colors"
                  title="Dejar de seguir"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {pagination && pagination.page < pagination.pages && (
        <div className="flex justify-center mt-6">
          <button onClick={loadMore} className="text-sm text-[#7b8fa8] hover:underline">
            Cargar más
          </button>
        </div>
      )}
    </div>
  );
};

export default Seguimiento;
